import clsx from 'clsx'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { CommonDataContext } from 'shared-components/contexts/CommonDataContext'
import { ScrollUpContext } from 'shared-components/contexts/ScrollUpContext'
import {
  CONVERSATION_DOCK_ATTR,
  ConversationContext,
} from 'shared-components/comments/conversation/ConversationContext'
import Linked from 'shared-components/service/Linked'
import HeaderAuth from 'shared-components/ui/HeaderAuth'
import { NOTIFICATIONS_ENTRY } from 'shared-components/ui/AuthMenu'
import { GearIcon, PersonIcon } from 'shared-components/ui/MenuIcons'
import { CommentHubAuthContext } from 'shared-components/contexts/AuthContext'
import { PopupContext } from 'shared-components/contexts/PopupContext'
import RWButton from 'shared-components/buttons/RWButton'
import Logo from 'shared-svg/LogoSG.svg'
import Percent from 'shared-svg/Percent.svg'
import Magnifier from 'shared-svg/Magnifier.svg'
import Headphone from 'shared-svg/Headphone.svg'
import CommentQuestion from 'shared-svg/CommentQuestion.svg'
import MobileNavTrigger, { isDrawerPopup } from 'components/nav/MobileNavTrigger'
import DealHuntSearchBox from 'components/search/DealHuntSearchBox'
import CategoryPanel from './CategoryPanel'
import EditorialPanel from './EditorialPanel'
import { dealHuntCategoryHref } from 'lib/dealhunt/url'
import { headerTabsOrFallback } from 'lib/header-nav/config'
import { prefetchHeaderNav, useHeaderNav } from 'lib/header-nav/use-header-nav'
import { communityHref, COMMUNITY_PATH } from 'lib/community/model'
import { useMemberNotificationLinks } from 'components/community/useMemberNotificationLinks'
import { bellNotificationLinks } from 'lib/community/hub-notifications'
import { useRememberedHubHandle } from 'components/community/useRememberedHubHandle'
import { HeaderAvatar } from 'components/community/AccountMenuIdentity'
import { StatsIcon, StatsNewPill } from 'components/community/AccountMenuStats'
import {
  COMMUNITY_ENTRY,
  CommunityCountPill,
  CommunityHeaderChip,
  PillWords,
} from 'components/community/CommunityEntry'
import { setCommunityUnread, useCommunityPill } from 'components/community/useCommunityPill'
import { accountMenuLinks, memberHeadPending } from 'lib/community/settings'
import { supportHref } from 'lib/owner-notes/support'
import { publicConfig } from 'config'
import panelStyles from './HeaderPanels.module.css'
import styles from './SiteHeader.module.css'

/** The Editorial tab is a panel like the category tabs, but has no catalogue node. */
const EDITORIAL_PANEL = 'editorial'

/**
 * Category tabs the desktop row leaves out. The Community chip (2026-10-06) took the
 * row's slack from the search field (`.search` shrinks first, down to 160px), so the
 * smallest category — Speakers, ~a tenth of the catalogue — gives its slot back. The
 * tab still exists everywhere else: the phone/tablet drawer, its hidden panel below
 * (crawlable links), search, and the editorial hubs.
 */
const DESKTOP_HIDDEN_TAB_IDS: ReadonlySet<string> = new Set(['speakers'])

/**
 * SoundGuys header — design frame "SoundGuys Header (4b) search left".
 *
 * ONE row: logo · search · category tabs + Editorial · bell · account, with a full-width
 * panel under it for whichever tab is open. The old second row (WP's `menuMain` behind
 * `DesktopNav`) is gone; its five editorial hubs now live inside the category panels,
 * scoped to the category, plus the one Editorial entry.
 *
 * ONE `panel` state for every tab, so opening one closes the others by construction
 * rather than by each tab watching its siblings. The panels are rendered here, as
 * siblings of the row inside <header> — see HeaderPanels.module.css for why they are not
 * portalled, button-anchored cards.
 */
const SiteHeader: React.VFC = () => {
  const {
    common: { homeLink, dealsButton, dealsLink, profileLink, editorialTabs },
    showAdminPanel,
  } = CommonDataContext.useContainer()
  // The tabs come with the page (common data, from the WordPress-owned tree), so server
  // and client render the same set; the compiled-in tabs only stand in when a page's
  // common data predates the field.
  const tabs = headerTabsOrFallback(editorialTabs)
  const rowTabs = tabs.filter(tab => !DESKTOP_HIDDEN_TAB_IDS.has(tab.id))
  const { scrolledUp } = ScrollUpContext.useContainer()
  const router = useRouter()
  const searchEnabled = publicConfig('searchEnabled')
  const trailingSlash = publicConfig('useTrailingSlash')
  const { doc, loading } = useHeaderNav()
  const onCommunity = router.pathname.startsWith(COMMUNITY_PATH)
  // The Community chip's count pill: public posts of the last 24 h / week, or the bell's unread
  // count for a member — after mount only, the header HTML is shared-cached (useCommunityPill).
  const communityPill = useCommunityPill()
  // The phone drawer docks UNDER the header and paints over the header's own avatar menu
  // (the popup layer sits above the sticky header). While the drawer is open the header
  // lifts above it — the drawer only, so a gallery or lightbox still covers the header.
  const { popupOpened, popupClassName } = PopupContext.useContainer()
  const drawerOpen = popupOpened && isDrawerPopup(popupClassName)
  // Phones, while the conversation bar shows (H2, 2026-10-25): the search row goes see-through
  // and the bar shows in its place when the header slides back; a search icon joins the row and
  // brings the field back. The header keeps its height — nothing on the page reflows.
  const barShown = ConversationContext.useOptionalContainer()?.barShown ?? false
  const [searchBack, setSearchBack] = useState(false)
  const mobileSearchRef = useRef<HTMLDivElement>(null)
  const conversationDock = searchEnabled && barShown && !searchBack
  useEffect(() => {
    if (!scrolledUp) {
      setSearchBack(false)
    }
  }, [scrolledUp])
  useEffect(() => {
    if (searchBack) {
      mobileSearchRef.current?.querySelector('input')?.focus()
    }
  }, [searchBack])
  // The reader's commentHub picture and name for the header avatar and the menu's slim head;
  // optional so a render outside the provider (a test) is just signed out.
  const reader = CommentHubAuthContext.useOptionalContainer()?.authState?.user ?? null
  const notificationLinks = useMemberNotificationLinks(reader?.id ?? null)
  // A reader without a member page (or before `/me` said so) gets the account Notifications page
  // (plan D1/D7) — `/profile/notifications` forwards a member to their hub tab anyway.
  const bellLinks = useMemo(
    () => bellNotificationLinks(notificationLinks.links, profileLink.href),
    [notificationLinks.links, profileLink.href]
  )
  const account = accountMenuLinks(notificationLinks.me, profileLink.href, trailingSlash)
  const readerName = reader?.name ?? ''
  const avatarHandle = useRememberedHubHandle(reader ? String(reader.id) : null, account.hubHandle)
  // Opens of the avatar menu: `/me` and the Stats entry's "NEW" read on an open (or the avatar's
  // hover intent) only — never per page view (Constraint a8b375cd).
  const [menuOpens, setMenuOpens] = useState(0)
  const { resolve: resolveMe, prefetch: prefetchMe } = notificationLinks
  const onMenuOpen = useCallback(() => {
    resolveMe()
    setMenuOpens(n => n + 1)
  }, [resolveMe])
  // A member whose `/me` is still on its way (this browser remembers their page): the head holds
  // its second line empty and already says "My page", rather than show the email and swap. Once
  // the read settled without an answer (failed, or no owner device) it is the non-member head.
  const memberPending = memberHeadPending(
    notificationLinks.me,
    avatarHandle,
    notificationLinks.read
  )

  const [panel, setPanel] = useState<string | null>(null)
  const headerRef = useRef<HTMLElement>(null)

  const close = useCallback(() => setPanel(null), [])
  const toggle = (id: string): void => setPanel(current => (current === id ? null : id))

  // Escape closes and returns focus to the trigger the browser still has focused.
  useEffect(() => {
    if (!panel) {
      return undefined
    }
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        close()
      }
    }
    const onPointer = (e: MouseEvent): void => {
      if (!headerRef.current?.contains(e.target as Node)) {
        close()
      }
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onPointer)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onPointer)
    }
  }, [panel, close])

  // A panel that survives a route change would hang over the page the reader just asked
  // for. Covers in-panel links, browser back, and anything else that navigates.
  useEffect(() => {
    router.events.on('routeChangeStart', close)
    return () => router.events.off('routeChangeStart', close)
  }, [router.events, close])

  // One field, one copy: the catalogue-count placeholder, everywhere the field renders.
  const searchPlaceholder = doc?.audioTotal
    ? `Search ${doc.audioTotal.toLocaleString('en-US')} products…`
    : undefined

  return (
    <header
      ref={headerRef}
      className={clsx(styles.header, {
        [styles.preview]: showAdminPanel,
        [styles.scrolledUp]: scrolledUp,
        [styles.noMobileSearch]: !searchEnabled,
        [styles.drawerOpen]: drawerOpen,
        [styles.conversationDock]: conversationDock,
      })}
    >
      <div className={styles.row} {...{ [CONVERSATION_DOCK_ATTR]: '' }}>
        <MobileNavTrigger searchEnabled={searchEnabled} className={styles.burger} />

        <Linked {...homeLink} className={styles.logoLink} aria-label="Home">
          <Logo className={styles.logoIcon} />
        </Linked>

        {searchEnabled && (
          <DealHuntSearchBox className={styles.search} placeholder={searchPlaceholder} />
        )}

        <nav className={styles.nav} aria-label="Categories" onPointerEnter={prefetchHeaderNav}>
          {/* The community home (owner reviews, leaderboard, members) — a tinted chip the
              shape of the deals button (design H3B, 2026-10-06), first in the row so it reads
              as a destination rather than a category; lit on any /community page. Its count
              pill (option G, 2026-10-18) says what the number is in the title and to a screen
              reader. */}
          <Linked
            href={communityHref(trailingSlash)}
            className={clsx(styles.tab, styles.communityChip, { [styles.tabActive]: onCommunity })}
            aria-current={onCommunity ? 'page' : undefined}
            title={communityPill?.label}
          >
            {COMMUNITY_ENTRY.title}
            {communityPill && (
              <>
                <CommunityCountPill pill={communityPill} />
                <PillWords pill={communityPill} />
              </>
            )}
          </Linked>
          {rowTabs.map(tab => (
            <button
              key={tab.id}
              type="button"
              className={clsx(styles.tab, { [styles.tabActive]: panel === tab.id })}
              aria-expanded={panel === tab.id}
              onClick={() => toggle(tab.id)}
            >
              {tab.label}
              <span className={styles.caret} aria-hidden="true">
                {panel === tab.id ? '▴' : '▾'}
              </span>
            </button>
          ))}
          <span className={styles.divider} aria-hidden="true" />
          <button
            type="button"
            className={clsx(styles.tab, { [styles.tabActive]: panel === EDITORIAL_PANEL })}
            aria-expanded={panel === EDITORIAL_PANEL}
            onClick={() => toggle(EDITORIAL_PANEL)}
          >
            {'Editorial'}
            <span className={styles.caret} aria-hidden="true">
              {panel === EDITORIAL_PANEL ? '▴' : '▾'}
            </span>
          </button>
        </nav>

        {/* Below xl the nav (and its Community chip) is in the drawer: the row keeps a
            compact Community chip beside the account control (owner funnel C13). */}
        <CommunityHeaderChip className={styles.mobileCommunity} active={onCommunity} />

        {conversationDock ? (
          <button
            type="button"
            className={styles.dockSearch}
            aria-label="Search"
            onClick={() => setSearchBack(true)}
          >
            <Magnifier className={styles.dockSearchIcon} aria-hidden="true" />
          </button>
        ) : null}

        {/* The evergreen /deals/ entry point, carried over from the header on main —
            the campaign bar below the header is a different, per-campaign surface. */}
        <RWButton
          variant="tertiary"
          size="medium"
          pLink={dealsLink}
          className={styles.dealsButton}
          ariaAttrs={{ 'aria-label': dealsButton.label }}
        >
          {dealsButton.label}
          <Percent className={styles.dealsIcon} />
        </RWButton>
        {/* The avatar menu (shared AuthMenu, one layout for both brands — SoundGuys passes
            data): the reader's letter disc with the unread badge; the head links their member
            page ("My page ›", tier · points · @handle) or, without one, /profile with their
            email; Notifications · Overview · My gear · Stats & badges · Settings; Help &
            support, Dark mode, Log out. The hub entries and the head's line come from `/me`,
            read only when the menu opens (Constraint a8b375cd) — until then, and for a reader
            without a page, Gear and Settings go through /profile, which forwards a member to the
            same tab. "Help & support" opens the reader's tickets with the current path as
            `?from=` (in-app navigation never updates `document.referrer`). */}
        <HeaderAuth
          className={styles.auth}
          notificationLinks={bellLinks}
          onMenuOpen={onMenuOpen}
          onMenuIntent={prefetchMe}
          onUnreadChange={setCommunityUnread}
          avatar={<HeaderAvatar handle={avatarHandle} name={readerName} />}
          identity={{
            avatar: <HeaderAvatar handle={avatarHandle ?? account.hubHandle} name={readerName} />,
            line: account.line ?? undefined,
            href: account.overview ?? undefined,
            cta: account.overview || memberPending ? 'My page' : undefined,
            pending: memberPending,
          }}
          primaryItems={[
            NOTIFICATIONS_ENTRY,
            // Overview and Stats only with a member page: a hidden or unclaimed profile has no
            // hub to open.
            ...(account.overview
              ? [{ id: 'overview', label: 'Overview', href: account.overview, icon: PersonIcon }]
              : []),
            {
              id: 'gear',
              label: 'My gear',
              href: account.gear.href,
              meta: account.gear.meta,
              icon: Headphone,
            },
            ...(account.stats
              ? [
                  {
                    id: 'stats',
                    label: 'Stats & badges',
                    href: account.stats,
                    icon: StatsIcon,
                    badge: <StatsNewPill opened={menuOpens} />,
                  },
                ]
              : []),
            { id: 'settings', label: 'Settings', href: account.settings, icon: GearIcon },
          ]}
          quietItems={[
            {
              id: 'support',
              label: 'Help & support',
              href: supportHref(trailingSlash, router.asPath),
              icon: CommentQuestion,
            },
          ]}
        />
      </div>

      {/* Mobile second row — the same search box the drawer used to own; the field opens
          its full-screen overlay on focus. Frame "SoundGuys Header Mobile". */}
      {searchEnabled && (
        <div ref={mobileSearchRef} className={styles.mobileSearch}>
          <DealHuntSearchBox variant="mobile" placeholder={searchPlaceholder} />
        </div>
      )}

      {/* Every panel is rendered and hidden with `hidden`, rather than mounted only when
          its tab is open. The category and coverage hrefs are derived from config, not
          from the header-nav fetch, so they are knowable server-side — but gating the
          mount on click state kept them out of the server HTML entirely, which left
          /product/* and the coverage hubs with NO crawlable inbound link from anywhere
          on the site. They are the only navigation into those routes. */}
      {tabs.map(tab => (
        <div key={tab.id} className={panelStyles.panel} hidden={panel !== tab.id}>
          <CategoryPanel
            config={tab}
            data={doc?.tabs.find(t => t.id === tab.id) ?? null}
            loading={loading}
            fallbackHref={dealHuntCategoryHref(tab.dealHuntSlug, trailingSlash)}
            trailingSlash={trailingSlash}
            onNavigate={close}
          />
        </div>
      ))}

      <div className={panelStyles.panel} hidden={panel !== EDITORIAL_PANEL}>
        <EditorialPanel columns={doc?.editorial ?? []} loading={loading} onNavigate={close} />
      </div>
    </header>
  )
}

export default SiteHeader
